import React from 'react';
import { Button, CurrencyInput, Fieldset, ModalBox, PriceString, RadioButton, useModalContext, useToastsContext, } from '@fourthwall/components';
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString';
import { formatCurrencyFromDecimal } from '../../utils';
import { SubscriptionCancelModalSkeleton } from './SubscriptionCancelModalSkeleton';
import { useSubscriptionCancelModal } from './hooks/useSubscriptionCancelModal';
import * as S from './styled';
import { CancelRequestType, FormFields, RefundRequestType } from './types';
export const SubscriptionCancelModal = ({ member, onSuccess }) => {
    const { form, refundAmount, lastCharge, isLoading, isFetching } = useSubscriptionCancelModal({
        member,
        onSuccess: (cancelRequestType, refundAmount) => {
            let copy = '';
            if (cancelRequestType === 'Now' && refundAmount) {
                copy =
                    'Refund issued and subscription cancelled. Please allow up to a minute for changes to show on this page.';
            }
            if (cancelRequestType === 'Schedule' && refundAmount) {
                copy =
                    'Refund issued and subscription scheduled to cancel. Please allow up to a minute for changes to show on this page.';
            }
            if (cancelRequestType === 'Now' && !refundAmount) {
                copy =
                    'Subscription cancelled. Please allow up to a minute for changes to show on this page.';
            }
            if (cancelRequestType === 'Schedule' && !refundAmount) {
                copy =
                    'Subscription scheduled to cancel. Please allow up to a minute for changes to show on this page.';
            }
            toast.notify(copy, { type: 'info' });
            modal.close();
            onSuccess();
        },
    });
    const modal = useModalContext();
    const toast = useToastsContext();
    if (isFetching)
        return React.createElement(SubscriptionCancelModalSkeleton, { member: member });
    return (React.createElement(ModalBox, { title: React.createElement(React.Fragment, null,
            "Cancel ",
            member.nickname,
            "'s ",
            member.tier?.name,
            " subscription"), size: "medium", buttons: [
            React.createElement(Button, { label: "Close", appearance: "secondary", onClick: modal.close }),
            React.createElement(Button, { label: refundAmount && lastCharge ? (React.createElement(React.Fragment, null,
                    "Confirm cancellation and refund",
                    ' ',
                    React.createElement(PriceString, { value: refundAmount, currency: lastCharge.currency.code }))) : ('Confirm cancellation'), appearance: "destructive", loading: isLoading, onClick: form.submitForm }),
        ], onClose: modal.close },
        React.createElement(S.Container, null,
            member.cancelRequest.type === 'Allowed' && (React.createElement(Fieldset, null,
                React.createElement(Fieldset.Row, null,
                    React.createElement(S.Bold, null, "When do you want to cancel this subscription?")),
                React.createElement(Fieldset.Row, null,
                    React.createElement(RadioButton, { label: React.createElement(React.Fragment, null,
                            "Remove access at end of cycle \u00A0",
                            React.createElement(TimezonedDateString, { date: member.cancelRequest.subscriptionCurrentPeriodEnd, format: "(MMM D, YYYY)" })), checkedValue: CancelRequestType.Schedule, disabled: isLoading, ...form.getFieldProps(FormFields.cancelRequestType) })),
                React.createElement(Fieldset.Row, null,
                    React.createElement(RadioButton, { label: "Remove access immediately", checkedValue: CancelRequestType.Now, disabled: isLoading, ...form.getFieldProps(FormFields.cancelRequestType) })))),
            lastCharge && (React.createElement(Fieldset, null,
                React.createElement(Fieldset.Row, null,
                    React.createElement(S.Bold, null, "Do you want to issue a refund for this subscription?")),
                lastCharge.refundRequest.type === 'Allowed' && (React.createElement(Fieldset.Row, null,
                    React.createElement(RadioButton, { label: React.createElement(React.Fragment, null,
                            "Full refund (",
                            React.createElement(PriceString, { value: formatCurrencyFromDecimal(lastCharge.refundRequest.options.full.amount, lastCharge.currency.decimalPlaces), currency: lastCharge.currency.code }),
                            ")"), checkedValue: RefundRequestType.full, disabled: isLoading, ...form.getFieldProps(FormFields.refundRequestType) }))),
                lastCharge.refundRequest.type === 'Allowed' &&
                    lastCharge.refundRequest.options.prorated.type !== 'NotAllowed' && (React.createElement(Fieldset.Row, null,
                    React.createElement(RadioButton, { label: React.createElement(React.Fragment, null,
                            "Pro-rated refund (",
                            React.createElement(PriceString, { value: formatCurrencyFromDecimal(lastCharge.refundRequest.options.prorated.amount, lastCharge.currency.decimalPlaces), currency: lastCharge.currency.code }),
                            ", based on ",
                            lastCharge.refundRequest.options.prorated.daysLeft,
                            " out of",
                            ' ',
                            lastCharge.refundRequest.options.prorated.daysTotal,
                            " days unused)"), checkedValue: RefundRequestType.prorated, disabled: lastCharge.refundRequest.options.prorated.type === 'Disabled' || isLoading, ...form.getFieldProps(FormFields.refundRequestType) }))),
                lastCharge.refundRequest.type === 'Allowed' && (React.createElement(Fieldset.Row, null,
                    React.createElement(RadioButton, { label: "Custom amount", checkedValue: RefundRequestType.custom, disabled: isLoading, ...form.getFieldProps(FormFields.refundRequestType) }))),
                lastCharge.refundRequest.type === 'Disabled' && (React.createElement(Fieldset.Row, null, {
                    AlreadyRefunded: 'Refund is not available. Last transaction for this member has been already refunded.',
                    TooSmallAmount: 'Refund is not available. The amount is smaller than the minimum refund amount ($1).',
                    IapCharge: 'Refund is not available. The subscription is managed by the mobile app operator (Android/iOS). Member has to request a refund from the in-app operator.',
                    TooOldPayPalCharge: 'Refund is not available. Due to PayPal policy, payments older than 6 months cannot be refunded.',
                }[lastCharge.refundRequest.reason])),
                form.values.refundRequestType === RefundRequestType.custom && (React.createElement(Fieldset.Row, { inner: true },
                    React.createElement(CurrencyInput, { label: "Custom amount", disabled: isLoading, ...form.getFieldProps(FormFields.customAmount) }))))),
            !lastCharge && (React.createElement(Fieldset, null,
                React.createElement(Fieldset.Row, null,
                    React.createElement(S.Bold, null, "Do you want to issue a refund for this subscription?")),
                React.createElement(Fieldset.Row, null, "There are no charges to refund."))),
            lastCharge?.refundRequest.type === 'Allowed' && (React.createElement("p", null,
                "Refunds cannot be undone. The refund amount",
                ' ',
                refundAmount ? (React.createElement(React.Fragment, null,
                    "of ",
                    React.createElement(PriceString, { value: refundAmount, currency: lastCharge.currency.code }),
                    ' ')) : undefined,
                "will be deducted from your account balance. Refunds will be returned within 48 hours to the original payment method used.")))));
};
